
import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    inquiryType: '',
    subject: '',
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inquiryTypes = [
    '제품 문의',
    '제조업체 문의',
    '견적 관련',
    '배송 문의',
    '기술 지원',
    '제휴 문의',
    '입점 문의',
    '기타'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      await fetch('https://readdy.ai/api/form/d67k99dt1k61917cleqg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          inquiryType: '',
          subject: '',
          companyName: '',
          contactPerson: '',
          phone: '',
          email: '',
          message: ''
        });
      }, 3000);
    } catch (error) {
      console.error('Submit error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">문의하기</h2>
        <p className="text-gray-600 text-lg">
          궁금하신 사항이나 도움이 필요하신 경우 언제든지 문의해주세요
        </p>
      </div>

      {/* Contact Info Cards */}
      <div className="grid md:grid-cols-3 gap-6 mb-12">
        <a href="tel:051-123-4567" className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group">
          <div className="w-14 h-14 bg-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
            <i className="ri-phone-line text-white text-2xl"></i>
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">전화 문의</h3>
          <p className="text-sm text-gray-600 mb-2">평일 09:00 - 18:00</p>
          <span className="text-teal-600 font-medium">
            051-123-4567
          </span>
        </a>
        <a href="mailto:contact@manseonhub.com" className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-2xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group">
          <div className="w-14 h-14 bg-cyan-600 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
            <i className="ri-mail-line text-white text-2xl"></i>
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">이메일 문의</h3>
          <p className="text-sm text-gray-600 mb-2">24시간 접수 가능</p>
          <span className="text-cyan-600 font-medium">
            contact@manseonhub.com
          </span>
        </a>
        <a href="https://pf.kakao.com" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-6 text-center hover:shadow-lg transition-all cursor-pointer group">
          <div className="w-14 h-14 bg-yellow-500 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
            <i className="ri-kakao-talk-fill text-white text-2xl"></i>
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">카카오톡 상담</h3>
          <p className="text-sm text-gray-600 mb-2">실시간 상담 가능</p>
          <span className="text-yellow-600 font-medium">
            @만선허브
          </span>
        </a>
      </div>

      {/* Quick Contact Buttons */}
      <div className="bg-gray-50 rounded-2xl p-6 mb-8">
        <h3 className="font-semibold text-gray-900 mb-4">빠른 문의</h3>
        <div className="flex flex-wrap gap-3">
          {inquiryTypes.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setFormData({ ...formData, inquiryType: type })}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                formData.inquiryType === type
                  ? 'bg-teal-600 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-teal-500 hover:text-teal-600'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Contact Form */}
      <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              문의 유형 <span className="text-red-500">*</span>
            </label>
            <select
              name="inquiryType"
              value={formData.inquiryType}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="">문의 유형 선택</option>
              {inquiryTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              제목 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder="문의 제목을 입력하세요"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              회사명/선박명 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              required
              placeholder="회사명 또는 선박명"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              담당자명 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              required
              placeholder="이름 입력"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              연락처 <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="010-0000-0000"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              이메일 <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="example@email.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            문의 내용 <span className="text-red-500">*</span>
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={6}
            maxLength={500}
            placeholder="문의하실 내용을 상세히 입력해주세요"
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
          ></textarea>
          <p className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</p>
        </div>

        <div className="flex items-center justify-center pt-4">
          <button
            type="submit"
            disabled={submitted || isSubmitting}
            className="inline-flex items-center gap-2 px-12 py-4 bg-teal-600 text-white rounded-full font-semibold hover:bg-teal-700 transition-all shadow-lg shadow-teal-600/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin text-xl"></i>
                전송 중...
              </>
            ) : submitted ? (
              <>
                <i className="ri-check-line text-xl"></i>
                문의 접수 완료
              </>
            ) : (
              <>
                <i className="ri-send-plane-line"></i>
                문의하기
              </>
            )}
          </button>
        </div>

        {submitted && (
          <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6 text-center">
            <i className="ri-checkbox-circle-line text-4xl text-teal-600 mb-3"></i>
            <p className="text-teal-800 font-medium">
              문의가 성공적으로 접수되었습니다. 빠른 시일 내에 답변드리겠습니다.
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
